import { accountLoadMessage } from "./utils/accountLoadMessage";
// src/MainApp.js

import { useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import {
  doc,
  getDoc,
} from "firebase/firestore";

import { auth, db } from "./firebase";
import { registerFacilityAccount } from "./services/access/facilityInvitations";
import { hasApprovedWorkerReadiness } from "./core/workforce/workerReadiness";

import Login from "./pages/Login";
import AdminDashboardEnterprise from "./pages/AdminDashboardEnterprise";
import FacilityPlanner from "./pages/FacilityPlanner";
import WorkerOnboarding from "./pages/WorkerOnboarding";
import WorkerDashboard from "./pages/WorkerDashboard";
import FacilityPulseMobile from "./pages/FacilityPulseMobile";

import {
  DateScopeProvider,
} from "./context/DateScopeContext";

import {
  applyInviteIfExists,
} from "./inviteApply";
import {
  repairLegacyFacilityAccessProfile,
  repairMissingFacilityAccessProfile,
} from "./finalizeFacilityMembership";

import {
  PLATFORM_ROLES,
  getUserRole,
  normalizePlatformRole,
  isPlatformAdmin,
} from "./security/permissions";

/*
 * ShiftBridge Canonical Application Bootstrap
 *
 * AUTHENTICATION
 *      ↓
 * users/{uid}
 *      +
 * accessProfiles/{uid}
 *      ↓
 * normalized authenticated profile
 *      ↓
 * application routing
 *
 * IMPORTANT
 * ---------
 * React must not manufacture another security identity.
 *
 * Owner/Admin remains Owner/Admin.
 * Planner remains Planner.
 * Facility selection is operational context, not authentication.
 */

/* =========================================================
   HELPERS
   ========================================================= */

function wait(milliseconds) {
  return new Promise((resolve) =>
    setTimeout(resolve, milliseconds)
  );
}

async function waitForUserProfile(
  userRef
) {
  let latestSnap = null;

  /*
   * Firebase Authentication can become ready before
   * signup/invite processing finishes writing users/{uid}.
   */
  for (
    let attempt = 0;
    attempt < 12;
    attempt += 1
  ) {
    latestSnap =
      await getDoc(userRef);

    if (latestSnap.exists()) {
      return latestSnap;
    }

    await wait(250);
  }

  return latestSnap;
}

async function loadAccessProfile(uid) {
  if (!uid) {
    return null;
  }

  const accessRef = doc(
    db,
    "accessProfiles",
    uid
  );

  const snap =
    await getDoc(accessRef);

  if (!snap.exists()) {
    return null;
  }

  return {
    ...snap.data(),
    uid,
  };
}

function isFacilityOperationalRole(
  role
) {
  const normalized =
    normalizePlatformRole(role);

  return [
    PLATFORM_ROLES.FACILITY_PLANNER,
    PLATFORM_ROLES.FACILITY_CLIENT,
    PLATFORM_ROLES.FACILITY_ADMIN_VIEWER,
  ].includes(normalized);
}


function accessProfileHasFacilityScope(
  accessProfile
) {
  if (
    !accessProfile ||
    typeof accessProfile !== "object"
  ) {
    return false;
  }

  const primaryFacilityId =
    String(
      accessProfile?.facilityId ||
        accessProfile?.primaryFacilityId ||
        ""
    )
      .trim()
      .toLowerCase();

  const facilityIds =
    Array.isArray(
      accessProfile?.facilityIds
    )
      ? accessProfile.facilityIds
          .map((value) =>
            String(value || "")
              .trim()
              .toLowerCase()
          )
          .filter(Boolean)
      : [];

  const allowedFacilityIds =
    Array.isArray(
      accessProfile?.allowedFacilityIds
    )
      ? accessProfile.allowedFacilityIds
          .map((value) =>
            String(value || "")
              .trim()
              .toLowerCase()
          )
          .filter(Boolean)
      : [];

  return Boolean(
    primaryFacilityId ||
      facilityIds.length > 0 ||
      allowedFacilityIds.length > 0
  );
}

function accessProfileIsUsable(
  accessProfile
) {
  if (!accessProfile) {
    return false;
  }

  if (
    accessProfile?.active === false ||
    accessProfile?.disabled === true
  ) {
    return false;
  }

  const tenantId =
    String(
      accessProfile?.tenantId || ""
    )
      .trim()
      .toLowerCase();

  const rawRole =
    String(
      accessProfile?.platformRole ||
        accessProfile?.role ||
        ""
    )
      .trim()
      .toLowerCase();

  const canonicalRole =
    normalizePlatformRole(
      rawRole
    );

  const permissions =
    accessProfile?.permissions;

  /*
   * Legacy accessProfiles used aliases such as:
   *
   *   planner
   *   facilityplanner
   *   facility
   *
   * Application code can normalize those aliases, but Firestore
   * authorization should operate on canonical stored data.
   *
   * Therefore a legacy alias is intentionally treated as needing
   * repair even though normalizePlatformRole() understands it.
   */
  const hasCanonicalStoredRole =
    Boolean(canonicalRole) &&
    rawRole === canonicalRole;

  /*
   * New canonical accessProfiles created by
   * finalizeFacilityMembership() carry these migration markers.
   *
   * Their absence tells us an older access record still needs to be
   * synchronized rather than accepted indefinitely.
   */
  const hasCanonicalVersions =
    Number(
      accessProfile?.scopeVersion
    ) >= 1 &&
    Number(
      accessProfile?.permissionVersion
    ) >= 1;

  const hasPermissions =
    permissions &&
    typeof permissions === "object" &&
    !Array.isArray(permissions);

  return Boolean(
    tenantId &&
      canonicalRole &&
      hasCanonicalStoredRole &&
      hasCanonicalVersions &&
      accessProfileHasFacilityScope(
        accessProfile
      ) &&
      hasPermissions
  );
}

/* =========================================================
   MAIN APPLICATION
   ========================================================= */

function MainAppInner() {
  const [booting, setBooting] =
    useState(true);

  const [user, setUser] =
    useState(null);

  const [profile, setProfile] =
    useState(null);

  const [error, setError] =
    useState("");

  /* =======================================================
     AUTH + PROFILE + ACCESS BOOTSTRAP
     ======================================================= */

  useEffect(() => {
    let loadVersion = 0;

    const unsub =
      onAuthStateChanged(
        auth,
        async (firebaseUser) => {
          const currentLoadVersion =
            ++loadVersion;

          setBooting(true);
          setError("");
          setProfile(null);

          try {
            /* =============================================
               SIGNED OUT
               ============================================= */

            if (!firebaseUser) {
              console.log(
                "AUDIT 1 — AUTH SIGNED OUT"
              );

              if (
                currentLoadVersion !==
                loadVersion
              ) {
                return;
              }

              setUser(null);
              return;
            }

            /* =============================================
               AUTH READY
               ============================================= */

           console.log(
  "AUDIT 1 — AUTH READY",
  {
    uid: firebaseUser.uid,
    email:
      firebaseUser.email ||
      "",

    authProjectId:
      auth?.app?.options?.projectId ||
      "",

    firestoreProjectId:
      db?.app?.options?.projectId ||
      "",

    firestoreDatabaseId:
      db?._databaseId?.database ||
      "(default)",

    userPath:
      `users/${firebaseUser.uid}`,
  }
);

            setUser(firebaseUser);

            const userRef = doc(
              db,
              "users",
              firebaseUser.uid
            );

            /* =============================================
               LOAD USER PROFILE
               ============================================= */

            let userSnap =
              await waitForUserProfile(
                userRef
              );

            if (
              currentLoadVersion !==
              loadVersion
            ) {
              return;
            }

            /*
             * Brand-new authenticated user:
             * attempt to claim an Admin-created invite.
             */
            if (!userSnap?.exists()) {
              console.log(
                "AUDIT 2 — PROFILE MISSING, CHECKING INVITE",
                {
                  uid:
                    firebaseUser.uid,
                  email:
                    firebaseUser.email ||
                    "",
                }
              );

              const inviteResult =
                await applyInviteIfExists(
                  firebaseUser
                );

              if (
                currentLoadVersion !==
                loadVersion
              ) {
                return;
              }

              if (
                !inviteResult?.applied
              ) {
                console.error(
                  "AUDIT 2 — PROFILE NOT FOUND",
                  {
                    uid:
                      firebaseUser.uid,
                    email:
                      firebaseUser.email ||
                      "",
                    inviteResult,
                  }
                );

                setError(
                  "User profile missing. Please contact admin."
                );

                return;
              }

              userSnap =
                await waitForUserProfile(
                  userRef
                );

              if (
                currentLoadVersion !==
                loadVersion
              ) {
                return;
              }

              if (
                !userSnap?.exists()
              ) {
                console.error(
                  "AUDIT 2 — PROFILE CREATION FAILED",
                  {
                    uid:
                      firebaseUser.uid,
                    email:
                      firebaseUser.email ||
                      "",
                  }
                );

                setError(
                  "User profile could not be created. Please contact admin."
                );

                return;
              }
            }

            /* =============================================
               EXISTING USER
               ============================================= */

            let userData =
              userSnap.data() || {};

            // The Auth observer may see the identity before access/membership
            // finishes. Join/resume the same registration before routing.
            if (userData.registrationSchemaVersion === 1 && !userData.facilityRegistrationComplete) {
              await registerFacilityAccount(firebaseUser, userData.inviteId);
              userSnap = await getDoc(userRef);
              userData = userSnap.data() || {};
              if (currentLoadVersion !== loadVersion) return;
            }

            let rawRole =
              normalizePlatformRole(
                userData?.platformRole ||
                  userData?.role ||
                  ""
              );

            /* =============================================
               LOAD ACCESS PROFILE
               ============================================= */

            let accessProfile = null;

            try {
              accessProfile =
                await loadAccessProfile(
                  firebaseUser.uid
                );
            } catch (accessError) {
              console.error(
                "AUDIT 2A — ACCESS PROFILE READ FAILED",
                accessError
              );

              throw new Error(
                "Unable to load operational access profile."
              );
            }

            if (
              currentLoadVersion !==
              loadVersion
            ) {
              return;
            }
/* =============================================
   REPAIR MISSING WORKER OPERATIONAL ACCESS
   ============================================= */

/*
 * A completed Worker account must have canonical
 * accessProfiles/{uid} authority before WorkerDashboard
 * starts its Firestore listeners.
 *
 * Workers who are still onboarding or who do not yet
 * have concrete facility scope are intentionally left
 * alone here.
 */
const workerOperationallyReady =
  rawRole ===
    PLATFORM_ROLES.WORKER &&
  hasApprovedWorkerReadiness(userData);

const workerHasFacilityScope =
  accessProfileHasFacilityScope(
    userData
  );

if (
  workerOperationallyReady &&
  workerHasFacilityScope &&
  !accessProfile
) {
  console.log(
    "AUDIT 2W — WORKER ACCESS PROFILE MISSING, REPAIR START",
    {
      uid:
        firebaseUser.uid,

      email:
        firebaseUser.email ||
        "",

      role:
        rawRole,

      tenantId:
        userData?.tenantId,

      facilityId:
        userData?.facilityId,

      primaryFacilityId:
        userData?.primaryFacilityId,

      facilityIds:
        userData?.facilityIds,
    }
  );

  try {
    const workerRepairResult =
      await repairMissingFacilityAccessProfile({
        uid:
          firebaseUser.uid,

        email:
          firebaseUser.email ||
          userData?.email ||
          "",

        userData,
      });

    console.log(
      "AUDIT 2W — WORKER ACCESS PROFILE REPAIR SUCCESS",
      workerRepairResult
    );
  } catch (
    workerAccessRepairError
  ) {
    console.warn(
      "AUDIT 2W — WORKER CANONICAL REPAIR FAILED, TRY INVITE",
      {
        uid:
          firebaseUser.uid,

        email:
          firebaseUser.email ||
          "",

        message:
          workerAccessRepairError
            ?.message ||
          String(
            workerAccessRepairError
          ),
      }
    );

    await applyInviteIfExists(
      firebaseUser
    );
  }

  if (
    currentLoadVersion !==
    loadVersion
  ) {
    return;
  }

  /*
   * Re-read both records because repair may have
   * canonicalized users/{uid} and created
   * accessProfiles/{uid}.
   */
  userSnap =
    await getDoc(userRef);

  if (userSnap.exists()) {
    userData =
      userSnap.data() ||
      userData;

    rawRole =
      normalizePlatformRole(
        userData?.platformRole ||
          userData?.role ||
          rawRole
      );
  }

  accessProfile =
    await loadAccessProfile(
      firebaseUser.uid
    );

  console.log(
    "AUDIT 2W — WORKER ACCESS PROFILE AFTER REPAIR",
    {
      exists:
        Boolean(
          accessProfile
        ),

      role:
        accessProfile?.role,

      tenantId:
        accessProfile?.tenantId,

      facilityId:
        accessProfile?.facilityId,

      facilityIds:
        accessProfile?.facilityIds,

      active:
        accessProfile?.active,

      scopeVersion:
        accessProfile?.scopeVersion,

      permissionVersion:
        accessProfile
          ?.permissionVersion,
    }
  );
}


            /* =============================================
               REPAIR EXISTING FACILITY ACCOUNT
               ============================================= */

            /*
             * Existing Planner / Facility Pulse accounts may
             * predate the canonical accessProfiles architecture.
             *
             * If their access record is missing or incomplete,
             * try to rebuild it from the original invitation
             * BEFORE starting facility listeners.
             *
             * This prevents the current error storm where
             * FacilityPlanner starts with incomplete authority.
             */
            if (
              isFacilityOperationalRole(
                rawRole
              ) &&
              !accessProfileIsUsable(
                accessProfile
              )
            ) {
              console.log(
                "AUDIT 2B — FACILITY ACCESS REPAIR REQUIRED",
                {
                  uid:
                    firebaseUser.uid,
                  email:
                    firebaseUser.email ||
                    "",
                  role: rawRole,
                  hasAccessProfile:
                    Boolean(
                      accessProfile
                    ),
                }
              );

                          let repairResult = null;

              /*
               * IMPORTANT:
               *
               * If an accessProfile already exists, do NOT search
               * the invites collection merely to canonicalize it.
               *
               * The existing access record is already an authority
               * record. The migration is only allowed to preserve
               * the same role family, tenant, and facility scope
               * while replacing legacy role aliases / permissions
               * with the canonical model.
               */
 
             if (accessProfile) {
  console.log(
    "AUDIT 2B1 — LEGACY ACCESS PROFILE REPAIR START",
    {
      uid:
        firebaseUser.uid,

      email:
        firebaseUser.email ||
        "",

      role:
        rawRole,
    }
  );

  repairResult =
    await repairLegacyFacilityAccessProfile({
      uid:
        firebaseUser.uid,

      email:
        firebaseUser.email ||
        userData?.email ||
        "",

      userData,

      accessProfile,
    });

  console.log(
    "AUDIT 2B1 — LEGACY ACCESS PROFILE REPAIR SUCCESS",
    repairResult
  );

  } else {
  /*
   * =====================================================
   * MISSING ACCESS PROFILE REPAIR
   * =====================================================
   *
   * Older ShiftBridge facility accounts may already have
   * a complete canonical users/{uid} identity and a valid
   * facility assignment, but no accessProfiles/{uid}.
   *
   * First attempt the stronger repair path that proves:
   *
   * users/{uid}
   *      ↓
   * canonical role + tenant + facility
   *      ↓
   * facilityProfiles/{facilityId}
   *      ↓
   * same tenant
   *      ↓
   * accessProfiles/{uid}
   *
   * If the user record itself is not complete enough for
   * that repair, fall back to trusted invitation
   * provisioning for genuinely new / partially registered
   * facility accounts.
   */

  console.log(
    "AUDIT 2B2 — ACCESS PROFILE MISSING, CANONICAL REPAIR START",
    {
      uid:
        firebaseUser.uid,

      email:
        firebaseUser.email ||
        "",

      role:
        rawRole,
    }
  );

  try {
    repairResult =
      await repairMissingFacilityAccessProfile({
        uid:
          firebaseUser.uid,

        email:
          firebaseUser.email ||
          userData?.email ||
          "",

        userData,
      });

    console.log(
      "AUDIT 2B2 — MISSING ACCESS PROFILE REPAIR SUCCESS",
      repairResult
    );
  } catch (missingAccessRepairError) {
    console.warn(
      "AUDIT 2B2 — CANONICAL MISSING ACCESS REPAIR NOT AVAILABLE",
      {
        uid:
          firebaseUser.uid,

        email:
          firebaseUser.email ||
          "",

        role:
          rawRole,

        message:
          missingAccessRepairError?.message ||
          String(
            missingAccessRepairError
          ),
      }
    );

    console.log(
      "AUDIT 2B2 — FALLING BACK TO INVITE PROVISIONING",
      {
        uid:
          firebaseUser.uid,

        email:
          firebaseUser.email ||
          "",

        role:
          rawRole,
      }
    );

    repairResult =
      await applyInviteIfExists(
        firebaseUser
      );
  }
}

              if (
                currentLoadVersion !==
                loadVersion
              ) {
                return;
              }

              console.log(
                "AUDIT 2B — FACILITY ACCESS REPAIR RESULT",
                repairResult
              );

              /*
               * Re-read both documents because invite application
               * may have normalized role/facility fields and
               * created accessProfiles/{uid}.
               */
              userSnap =
                await getDoc(userRef);

              if (
                userSnap.exists()
              ) {
                userData =
                  userSnap.data() ||
                  userData;

                rawRole =
                  normalizePlatformRole(
                    userData?.platformRole ||
                      userData?.role ||
                      rawRole
                  );
              }

              accessProfile =
                await loadAccessProfile(
                  firebaseUser.uid
                );

              if (
                currentLoadVersion !==
                loadVersion
              ) {
                return;
              }
            }

           /* =============================================
   BUILD CANONICAL AUTHENTICATED PROFILE
   ============================================= */

/*
 * This is the single runtime identity object passed
 * into the ShiftBridge application.
 *
 * Firebase Auth owns uid/email identity.
 * users/{uid} owns profile/lifecycle information.
 * accessProfiles/{uid}, when present, owns
 * operational authorization.
 */
// UI scope mirrors the protected platform authority; role-family routing is unchanged.
const platformAdminSnap = await getDoc(doc(db, "platformAdmins", firebaseUser.uid));
const platformAdminData = platformAdminSnap.exists() ? platformAdminSnap.data() : {};
const trustedPlatformAdmin = platformAdminData.active === true &&
  platformAdminData.disabled !== true && platformAdminData.role === "platform_admin";

const loadedProfile = {
  ...userData,
  trustedPlatformAdmin,

  uid:
    firebaseUser.uid,

  email:
    firebaseUser.email ||
    userData?.email ||
    "",

  accessProfile:
    accessProfile || null,
};

/*
 * Resolve operational role from the same canonical
 * profile that the rest of ShiftBridge will receive.
 *
 * getUserRole() now makes accessProfiles/{uid}
 * authoritative when one exists.
 */
const resolvedRole =
  getUserRole(
    loadedProfile
  );

/* =============================================
   CANONICAL ROLE CONSISTENCY CHECK
   ============================================= */

/*
 * users/{uid} owns profile/lifecycle identity.
 * accessProfiles/{uid}, when present, owns
 * operational authorization.
 *
 * They may use historical aliases, but after
 * normalizePlatformRole() they must not describe
 * two different account roles.
 */
const userDocumentRole =
  normalizePlatformRole(
    userData?.platformRole ||
      userData?.role ||
      ""
  );

const accessDocumentRole =
  accessProfile
    ? normalizePlatformRole(
        accessProfile?.platformRole ||
          accessProfile?.role ||
          ""
      )
    : "";

/*
 * An existing access profile with an actual role
 * that contradicts users/{uid} is not something
 * routing should silently repair or guess around.
 *
 * Stop bootstrap and surface the provisioning
 * defect so Admin can correct the membership.
 */
if (
  accessProfile &&
  userDocumentRole &&
  accessDocumentRole &&
  userDocumentRole !==
    accessDocumentRole
) {
  console.error(
    "AUDIT 2E — IDENTITY ROLE CONFLICT",
    {
      uid:
        firebaseUser.uid,

      email:
        firebaseUser.email ||
        "",

      userDocumentRole,

      accessDocumentRole,

      resolvedRole,
    }
  );

  setError(
    "This account has conflicting operational identity records. Please contact admin."
  );

  return;
}

/*
 * rawRole is retained only as a bootstrap/migration
 * hint for an older facility account whose existing
 * access profile is incomplete.
 *
 * It is NOT the runtime authorization role.
 */
const bootstrapFacilityRole =
  resolvedRole || rawRole;


/* =============================================
   FACILITY ACCOUNT ACCESS GATE
   ============================================= */

if (
  isFacilityOperationalRole(
    bootstrapFacilityRole
  )
) {
  if (!accessProfile) {
    console.error(
      "AUDIT 2C — FACILITY ACCESS PROFILE MISSING",
      {
        uid:
          firebaseUser.uid,

        email:
          firebaseUser.email ||
          "",

        role:
          bootstrapFacilityRole,
      }
    );

    setError(
      "This facility account is missing its operational access profile. Please contact admin."
    );

    return;
  }

  if (
    accessProfile?.active ===
      false ||
    accessProfile?.disabled ===
      true
  ) {
    console.error(
      "AUDIT 2C — FACILITY ACCESS DISABLED",
      {
        uid:
          firebaseUser.uid,

        email:
          firebaseUser.email ||
          "",

        role:
          bootstrapFacilityRole,
      }
    );

    setError(
      "This facility account is currently disabled. Please contact admin."
    );

    return;
  }

  if (
    !accessProfileIsUsable(
      accessProfile
    )
  ) {
    console.error(
      "AUDIT 2C — FACILITY ACCESS PROFILE INCOMPLETE",
      {
        uid:
          firebaseUser.uid,

        email:
          firebaseUser.email ||
          "",

        role:
          bootstrapFacilityRole,

        resolvedRole,

        accessProfile,
      }
    );

    setError(
      "This facility account has incomplete operational access. Please contact admin."
    );

    return;
  }
} 
            console.log(
              "AUDIT 2 — PROFILE READY",
              {
                uid:
                  loadedProfile.uid,

                email:
                  loadedProfile.email,

                role:
                  loadedProfile.role,

                platformRole:
                  loadedProfile.platformRole,

                resolvedRole,

                tenantId:
                  loadedProfile.tenantId,

                facilityId:
                  loadedProfile.facilityId,

                primaryFacilityId:
                  loadedProfile.primaryFacilityId,

                facilityIds:
                  loadedProfile.facilityIds,

                allowedFacilityIds:
                  loadedProfile.allowedFacilityIds,

                hasAccessProfile:
                  Boolean(
                    loadedProfile.accessProfile
                  ),

                accessRole:
                  loadedProfile
                    ?.accessProfile
                    ?.role,

                accessTenantId:
                  loadedProfile
                    ?.accessProfile
                    ?.tenantId,

                accessFacilityId:
                  loadedProfile
                    ?.accessProfile
                    ?.facilityId,

                accessFacilityIds:
                  loadedProfile
                    ?.accessProfile
                    ?.facilityIds,

                accessActive:
                  loadedProfile
                    ?.accessProfile
                    ?.active,

                status:
                  loadedProfile.status,

                workerStatus:
                  loadedProfile.workerStatus,
              }
            );

            console.log(
  "AUDIT 2D — ACCESS PROFILE JSON",
  JSON.stringify(
    loadedProfile?.accessProfile || null,
    null,
    2
  )
);

            setProfile(
              loadedProfile
            );
          } catch (err) {
            if (
              currentLoadVersion !==
              loadVersion
            ) {
              return;
            }

            console.error(
              "MainApp auth/profile/access load error:",
              err
            );

            setError(accountLoadMessage(err));
          } finally {
            if (
              currentLoadVersion ===
              loadVersion
            ) {
              setBooting(false);
            }
          }
        }
      );

    return () => {
      loadVersion += 1;
      unsub();
    };
  }, []);

  /* =======================================================
     LOGOUT
     ======================================================= */

  async function handleLogout() {
    try {
      await signOut(auth);
    } catch (err) {
      console.error(
        "Logout error:",
        err
      );

      setError(
        err?.message ||
          "Logout failed."
      );
    }
  }

  /* =======================================================
     BOOTING
     ======================================================= */

  if (booting) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: "#f8fafc",
          color: "#0f172a",
          fontWeight: 800,
          fontSize: 16,
        }}
      >
        Loading ShiftBridge…
      </div>
    );
  }

  /* =======================================================
     SIGNED OUT
     ======================================================= */

  if (!user) {
    return <Login />;
  }

  /* =======================================================
     ACCOUNT / ACCESS ERROR
     ======================================================= */

  if (error && !profile) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: "#f8fafc",
          padding: 20,
        }}
      >
        <div
          style={{
            maxWidth: 520,
            width: "100%",
            background: "#fff",
            border:
              "1px solid #e5e7eb",
            borderRadius: 16,
            padding: 20,
            boxShadow:
              "0 8px 22px rgba(15,23,42,.05)",
          }}
        >
          <h2
            style={{
              color: "#991b1b",
            }}
          >
            Account Error
          </h2>

          <p>{error}</p>

          <button
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div
        className="center-screen"
      >
        <div className="card">
          <h2>
            Preparing Your Account
          </h2>

          <p>
            ShiftBridge is preparing
            your account. Please wait
            and refresh.
          </p>
        </div>
      </div>
    );
  }

  /* =======================================================
     CANONICAL ROUTING ROLE
     ======================================================= */

const resolvedRole =
  getUserRole(profile);

const isWorker =
  resolvedRole ===
    PLATFORM_ROLES.WORKER;

  console.log(
    "AUDIT 3 — ROUTE DECISION",
    {
      email:
        profile?.email,

      resolvedRole,

      role:
        profile?.role,

      platformRole:
        profile?.platformRole,

      tenantId:
        profile?.tenantId,

      facilityId:
        profile?.facilityId,

      primaryFacilityId:
        profile?.primaryFacilityId,

      facilityIds:
        profile?.facilityIds,

      allowedFacilityIds:
        profile?.allowedFacilityIds,

      hasAccessProfile:
        Boolean(
          profile?.accessProfile
        ),

      accessRole:
        profile
          ?.accessProfile
          ?.role,

      accessFacilityId:
        profile
          ?.accessProfile
          ?.facilityId,
    }
  );

  /* =======================================================
     WORKER REGISTRATION GATE
     ======================================================= */

  const registrationApproved =
    profile?.registrationStatus ===
      "approved" ||
    profile?.canAccessOnboarding ===
      true;

  if (
    isWorker &&
    !registrationApproved
  ) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: "#f8fafc",
          padding: 20,
        }}
      >
        <div
          style={{
            maxWidth: 520,
            width: "100%",
            background: "#fff",
            border:
              "1px solid #e5e7eb",
            borderRadius: 16,
            padding: 20,
            boxShadow:
              "0 8px 22px rgba(15,23,42,.05)",
          }}
        >
          <h2>
            Registration Received
          </h2>

          <p>
            Your account registration
            has been received and is
            awaiting admin approval.
          </p>

          <button
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  /* =======================================================
     FACILITY PULSE / CLIENT
     ======================================================= */

  if (
    resolvedRole ===
      PLATFORM_ROLES.FACILITY_CLIENT ||
    resolvedRole ===
      PLATFORM_ROLES.FACILITY_ADMIN_VIEWER
  ) {
    return (
      <FacilityPulseMobile
        profile={profile}
      />
    );
  }

/* =======================================================
   PLATFORM ADMIN
   =======================================================
 *
 * Platform-admin membership is determined by the
 * canonical security authority.
 *
 * This includes:
 *
 * - owner_admin
 * - finance_admin
 * - operations_admin
 * - hr_admin
 * - credentialing_admin
 * - agency_admin
 *
 * Individual Admin features remain permission-gated.
 */

if (
  isPlatformAdmin(profile)
) {
  return (
    <AdminDashboardEnterprise
      profile={profile}
    />
  );
}

  /* =======================================================
     FACILITY PLANNER
     ======================================================= */

  if (
    resolvedRole ===
    PLATFORM_ROLES.FACILITY_PLANNER
  ) {
    return (
      <FacilityPlanner
        profile={profile}
      />
    );
  }

  /* =======================================================
     WORKER
     ======================================================= */

  if (isWorker) {
    return (
      hasApprovedWorkerReadiness(profile)
    ) ? (
      <WorkerDashboard
        user={user}
        userProfile={profile}
      />
    ) : (
      <WorkerOnboarding
        user={user}
        userProfile={profile}
      />
    );
  }

  /* =======================================================
     UNKNOWN ROLE
     ======================================================= */

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "#f8fafc",
        padding: 20,
      }}
    >
      <div
        style={{
          maxWidth: 520,
          width: "100%",
          background: "#fff",
          border:
            "1px solid #e5e7eb",
          borderRadius: 16,
          padding: 20,
        }}
      >
        <h2>Unknown Role</h2>

        <p>
          This account does not have
          a supported ShiftBridge role
          yet.
        </p>

        <p>
          Current role:{" "}
          {resolvedRole ||
            profile?.role ||
            "none"}
        </p>

        <button
          onClick={handleLogout}
        >
          Logout
        </button>
      </div>
    </div>
  );
}

/* =========================================================
   APP ROOT
   ========================================================= */

export default function MainApp() {
  return (
    <DateScopeProvider>
      <MainAppInner />
    </DateScopeProvider>
  );
}
